import { tl } from 'common/services/i18nService';
import { BandedCriterion, CriterionScoreDetail } from 'grading/src/features/setMark/reducer';
import { create } from 'xmlbuilder2';
import { SessionState } from 'common/store/session/reducer';
import { ContextState } from '../../store/context/reducer';
import { EditorMode } from '../../store/context/types';
import { RubricType } from './types';
import { EditorState, LockedByInfo } from '../../features/rubricEditor/reducer';

export const initialBand = {
    title: tl('rubrics.header.newLevel'),
    description: '',
    minScore: 0,
    maxScore: 0,
};

export const getRubricsXmlLocalStorageKey = (contentItemId: number) => {
    return `rubricsTableSetup_${contentItemId ? `_${contentItemId}` : ''}`;
};

export const capitalizeWord = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export const convertSetupToXml = (tableSetup: BandedCriterion[], isAscendingOrder: boolean) => {
    const rubricType = tableSetup[0].rubricType;
    const isPercentage: boolean = rubricType === RubricType.PERCENTAGE;
    const minAttribute = !isPercentage ? '@data-reward-minimum-marks' : '@data-reward-minimum-percentage';
    const maxAttribute = !isPercentage ? '@data-maximum-marks' : '@data-maximum-percentage';
    const doc = create({
        itemBody: {
            '@inspera:defaultLanguage': 'en_us',
            '@inspera:supportedLanguages': 'en_us',
            'rubricBlock': [
                {
                    '@use': 'rubrics_marking',
                    '@view': 'scorer',
                    '@bandDirection': isAscendingOrder ? 'ltr' : 'rtl',

                    'div': tableSetup?.map((item: BandedCriterion) => {
                        const criterionDetails = item.criterionScoreDetails.map((criterion: CriterionScoreDetail) => {
                            return {
                                [minAttribute]: criterion?.minScore,
                                h3: criterion?.title,
                                div: criterion?.description.replace(/<br>/g, '\n'),
                            };
                        });

                        return {
                            '@data-aspect-id': `${item.id}`,
                            [maxAttribute]: `${item.maxMark}`,
                            'h2': `${item.title}`,
                            'ul': { li: criterionDetails },
                        };
                    }),
                    ...rubricType && { '@inspera:rubricsType': rubricType },
                },
            ],
        },
    });
    return doc.end({ prettyPrint: true }).replace('<?xml version="1.0"?>', '');
};

export const isReadOnlyMode = (
    featureState: EditorState,
    contextState: ContextState,
    sessionState: SessionState
) => {
    const { lockedByInfo } = featureState;
    return contextState.mode === EditorMode.PREVIEW_MODE
        || contextState.mode === EditorMode.ADD_TO_QUESTION_MODE
        || contextState.mode === EditorMode.STUDENT_MODE
        || (isEditMode(contextState)
            && (isLockedByDifferentUser(lockedByInfo, sessionState) || !hasPermissionToEditRubric(sessionState)));
};

export const hasPermissionToEditRubric = (sessionState: SessionState) => {
    const privileges = sessionState?.user?.privileges || [];
    const customPrivileges = sessionState?.user?.customPrivileges || [];
    const hasExtendedAccess = privileges?.includes('system_role_testextendedcontentadmin_rel');
    const hasAuthor = privileges?.includes('system_role_testauthor_rel');
    const hasRubricEditViaCustomRole = customPrivileges?.some((item) => item.name === 'write_rubric_templates');
    return hasAuthor || hasExtendedAccess || hasRubricEditViaCustomRole;
}

export const isAddToQuestionMode = (contextState: ContextState) => {
    return contextState.mode === EditorMode.ADD_TO_QUESTION_MODE;
};

export const isUsedForMarkingMode = (contextState: ContextState) => {
    return contextState.mode === EditorMode.USED_FOR_MARKING_MODE;
};

export const isStudentMode = (contextState: ContextState) => {
    return contextState.mode === EditorMode.STUDENT_MODE;
};

export const isEditMode = (contextState: ContextState) => {
    return contextState.mode === EditorMode.EDIT_MODE;
};

export const isLockedByDifferentUser = (lockedByInfo: LockedByInfo, sessionState: SessionState) => {
    return lockedByInfo
        && lockedByInfo.locked
        && lockedByInfo.modifiedBy
        && lockedByInfo.modifiedBy.partyId !== sessionState.user.id;
};

export const inIframe = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

export const getTableStateFromRubricXml = (criteriaContent: BandedCriterion[]) => {
    const maxValue = criteriaContent.reduce((acc: number, curr: BandedCriterion) => (acc + curr.maxMark), 0);
    const isPercentage = criteriaContent[0]?.rubricType === RubricType.PERCENTAGE;
    if (criteriaContent.length) {
        return {
            headerRows: [`${maxValue} ${isPercentage ? tl('rubrics.table.percentageTotal') : tl('rubrics.table.poinsTotal')}`,
                ...criteriaContent[0]?.criterionScoreDetails.map((item) => item.title)],
            bodyRows: [...criteriaContent],
            maxValue,
        };
    }
    return null;
};

export const isFloat = (value: number) => {
    if (
        typeof value === 'number'
      && !Number.isNaN(value)
      && !Number.isInteger(value)
    ) {
        return true;
    }

    return false;
};
